import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from "react";
import { useReducedMotion } from "framer-motion";
import { FREE_PATTERNS, buildFreePattern, freePatternSpanDeg } from "../../shared/api-contract";
import type {
  DiamondClarity, DiamondOrigin, FreePatternId, FreeStone, GiaColorGrade,
} from "../../shared/api-contract";
import { useRing } from "./ringContext";
import { useCaps } from "../editor/CapsProvider";
import { freeStoneCarat, freeStoneSettingChips, freeStoneSizePatch } from "./junctionUi";
import { useViewerEvent } from "../../state/hooks";

import { Button, Chip, Disclosure, Field, NumberField, OptionCard, SectionLabel, Select, Slider } from "../kit";



const STONE_TYPE_OPTIONS: { id: NonNullable<FreeStone["type"]>; label: string }[] = [
  { id: "diamond", label: "Diamond" },
  { id: "goldBead", label: "Gold bead" },
];


/**
 * Ring Builder › Gems › Setting Type "Free" — the unrolled-band editor (v40).
 *
 * The band is drawn flat: the strip's LENGTH is 0°→360° around the ring, its
 * WIDTH is `u` (−1 … +1, the same normalised convention as `rowPosition`). One
 * marker per `FreeStone`; drag it, click empty strip to add one, Delete to drop
 * it. The viewer stays the single source of truth throughout — nothing here
 * keeps its own copy of the arrangement, only the in-flight drag position.
 *
 * Not drawn to scale: a real 4 mm band unrolled at 0°→360° is a 50:1 ribbon
 * nobody can hit with a trackpad. The strip is a usable aspect; the caption
 * says so, and every number the editor emits is exact.
 */

// ─── strip geometry, in SVG user units (the viewBox scales to the panel) ────
const VB_W = 260;
const VB_H = 420;
const X0 = 40;
const X1 = 252;
const Y0 = 12;
const Y1 = 408;
const SW = X1 - X0;
const SH = Y1 - Y0;
/** ~18 px across at the rendered size — grabbable on a trackpad. */
const MARKER_R = 9;
const TICKS = [0, 90, 180, 270, 360];

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
const xOf = (u: number) => X0 + ((u + 1) / 2) * SW;
const yOf = (deg: number) => Y0 + (deg / 360) * SH;

/** The repeat row starts at 1…4 and GROWS: every "+ Add" adds the next chip, as
 *  the reference's does (its capture reaches 11). 24 is `clampFreeRepeat`'s cap. */
const REPEAT_BASE = 4;
const REPEAT_MAX = 24;

/** Absent colour grade / clarity is "unspecified" (the contract's own default),
 *  which prices at the base grade and prints nothing on the spec sheet. */
const UNSPECIFIED = { id: "", label: "Unspecified" };

export function FreeStoneEditor() {
  const { state, catalogs, r, apply, beginDraft, endDraft } = useRing();
  const caps = useCaps();
  const reduce = useReducedMotion();

  const stones = state.freeStones ?? [];
  const repeat = clamp(Math.round(state.freeRepeat ?? 1), 1, REPEAT_MAX);
  const rotate = state.freeRotateDeg ?? 0;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Item 22's two controls re-stamp the LAST pattern picked, so they need to
  // remember which one that was. Local state, not derived: `freeStones` is a
  // plain arrangement once stamped and carries no memory of the tile it came
  // from — by design, since the user is free to drag the stones afterwards.
  const [lastPattern, setLastPattern] = useState<FreePatternId | null>(null);
  const [patternCount, setPatternCount] = useState(15);
  const [patternSpan, setPatternSpan] = useState(() => Math.round(freePatternSpanDeg("wave")));

  // ITEM 24 — "Option to click each diamond and change its stuff means change
  // size and all". The click happens on the viewer's own canvas, inside the
  // iframe, so it arrives as an event rather than something the host can ask
  // about. Selecting here is all that is needed: the Selected Stone panel below
  // already edits size, type and colour.
  useViewerEvent("freeStoneSelectionChanged", useCallback(({ id }: { id: string | null }) => {
    setSelectedId(id);
  }, []));
  const [hoverId, setHoverId] = useState<string | null>(null);
  /** In-flight drag position, so the marker tracks the pointer at pointer speed
   *  instead of RPC speed. Cleared on drop; state takes over again. */
  const [drag, setDrag] = useState<{ id: string; thetaDeg: number; u: number } | null>(null);

  const svgRef = useRef<SVGSVGElement>(null);
  const draftOpen = useRef(false);

  const selected = stones.find((s) => s.id === selectedId) ?? null;

  // Draft batching is ref-counted, so it must be balanced even if the tab
  // unmounts mid-drag — otherwise the viewer is stranded at 64 segments.
  const openDraft = () => {
    if (draftOpen.current) return;
    draftOpen.current = true;
    beginDraft();
  };
  const closeDraft = () => {
    if (!draftOpen.current) return;
    draftOpen.current = false;
    endDraft();
  };
  useEffect(() => () => {
    if (!draftOpen.current) return;
    draftOpen.current = false;
    endDraft();
  }, [endDraft]);

  /** Pointer → (angle, offset). No rotation/skew on the SVG, so the bounding
   *  box ratio is the whole transform. */
  const atEvent = (e: { clientX: number; clientY: number }) => {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box || !box.width || !box.height) return { thetaDeg: 0, u: 0 };
    const x = ((e.clientX - box.left) / box.width) * VB_W;
    const y = ((e.clientY - box.top) / box.height) * VB_H;
    return {
      thetaDeg: (clamp(((y - Y0) / SH) * 360, 0, 360)) % 360,
      u: clamp(((x - X0) / SW) * 2 - 1, -1, 1),
    };
  };

  const addAt = async (at: { thetaDeg: number; u: number }) => {
    const before = new Set(stones.map((s) => s.id));
    const next = await r.addFreeStone(at);
    if (!next) return;
    await apply(Promise.resolve(next));
    setSelectedId(next.freeStones?.find((s) => !before.has(s.id))?.id ?? null);
  };

  const remove = (id: string) => {
    setSelectedId((cur) => (cur === id ? null : cur));
    void apply(r.removeFreeStone(id));
  };

  const startDrag = (e: ReactPointerEvent<SVGCircleElement>, s: FreeStone) => {
    e.stopPropagation();
    // Capture on the marker itself: a drag that leaves the canvas keeps
    // tracking, and the trailing `click` cannot reach the strip's add handler.
    e.currentTarget.setPointerCapture(e.pointerId);
    setSelectedId(s.id);
    setDrag({ id: s.id, thetaDeg: s.thetaDeg, u: s.u });
    openDraft();
  };

  const moveDrag = (e: ReactPointerEvent<SVGCircleElement>) => {
    if (!drag) return;
    const at = atEvent(e);
    setDrag({ id: drag.id, ...at });
    // Live preview: fire-and-forget (the engine debounces the rebuild) and let
    // `ringStateChanged` reconcile. The final value is committed on drop.
    void r.updateFreeStone(drag.id, at);
  };

  const endDrag = () => {
    if (!drag) return;
    const { id, thetaDeg, u } = drag;
    setDrag(null);
    // Commit BEFORE leaving draft mode: RPCs are ordered, so the one high-res
    // rebuild endDraft triggers already has the final position.
    void apply(r.updateFreeStone(id, { thetaDeg, u }));
    closeDraft();
  };

  const nudge = (s: FreeStone, dTheta: number, dU: number) =>
    void apply(r.updateFreeStone(s.id, { thetaDeg: s.thetaDeg + dTheta, u: s.u + dU }));

  const onMarkerKey = (e: ReactKeyboardEvent<SVGCircleElement>, s: FreeStone) => {
    const big = e.shiftKey;
    switch (e.key) {
      case "Delete":
      case "Backspace": e.preventDefault(); remove(s.id); break;
      case "ArrowUp": e.preventDefault(); nudge(s, big ? -10 : -2, 0); break;
      case "ArrowDown": e.preventDefault(); nudge(s, big ? 10 : 2, 0); break;
      case "ArrowLeft": e.preventDefault(); nudge(s, 0, big ? -0.2 : -0.05); break;
      case "ArrowRight": e.preventDefault(); nudge(s, 0, big ? 0.2 : 0.05); break;
      case "Enter":
      case " ": e.preventDefault(); setSelectedId(s.id); break;
      default: break;
    }
  };

  // The pattern generator works in PITCH UNITS; these two are what size a stamp
  // to this band. Identical to `BandConfigurator.applyFreePattern`, so a
  // thumbnail is a picture of the arrangement the tile actually stamps.
  const stoneMM = state.diamond.stoneSizeMM;
  const pitchMM = stoneMM * 1.35;
  const placeR = (state.spec?.innerDiameterMM ?? 17.35) / 2 + state.thicknessMM;
  const halfSpan = Math.max(1e-3, state.widthMM / 2 - stoneMM / 2);
  const stepDeg = (pitchMM / placeR) * (180 / Math.PI);
  const stepU = pitchMM / halfSpan;

  // Tiles are DRAWN from the contract constant (it carries `count`, which the served
  // catalog does not, and the thumbnail must be a picture of the arrangement the tile
  // actually stamps) but GATED on the catalog, so we never offer a pattern the connected
  // viewer cannot stamp. An older viewer serving no catalog gets the full set — the whole
  // editor is already behind the v40 caps flag.
  const served = catalogs.freePatterns;
  const servedPatterns = served?.length
    ? FREE_PATTERNS.filter((p) => served.some((c) => c.id === p.id))
    : FREE_PATTERNS;

  const d = state.diamond;
  const specSummary = [
    d.origin === "lab" ? "Lab grown" : "Natural",
    d.colorGrade,
    d.clarity,
  ].filter(Boolean).join(" · ");
  const beads = state.spec?.beadCount ?? 0;
  const countLine = [
    `${state.spec?.stoneCount ?? 0} stone${(state.spec?.stoneCount ?? 0) === 1 ? "" : "s"}`,
    `${(state.spec?.totalCarat ?? 0).toFixed(3)} ct total`,
    beads ? `${beads} gold bead${beads > 1 ? "s" : ""}` : "",
  ].filter(Boolean).join(" · ");

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <SectionLabel>Unrolled Band</SectionLabel>

        <svg
          ref={svgRef}
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          className="w-full max-w-[320px] self-center touch-none select-none"
          role="group"
          aria-label="Unrolled band. Drag a stone to move it, or click the strip to place one."
        >
          {/* the band itself — the only thing that adds a stone.
              Focusable with a key handler as well as clickable: pointer-only
              placement would leave a keyboard user unable to add a stone at all.
              Enter/Space drops one at the centre, which is then nudgeable with
              the arrow keys like any other marker. */}
          <rect
            x={X0}
            y={Y0}
            width={SW}
            height={SH}
            rx={10}
            className="fill-surface-container-lowest stroke-outline-variant focus:outline-none focus-visible:stroke-primary"
            strokeWidth={1}
            tabIndex={0}
            role="button"
            aria-label="Band surface — press Enter to add a stone at the centre, then use the arrow keys to position it"
            onClick={(e) => void addAt(atEvent(e))}
            onKeyDown={(e) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              e.preventDefault();
              void addAt({ thetaDeg: 0, u: 0 });
            }}
            style={{ cursor: "copy" }}
          />

          {/* degree ruler + centre line, over the strip and inert to the pointer */}
          <g pointerEvents="none">
            {TICKS.map((deg) => (
              <g key={deg} className="text-outline">
                <text
                  x={X0 - 8}
                  y={yOf(deg) + 3.5}
                  textAnchor="end"
                  fill="currentColor"
                  className="font-body text-[9px] tabular-nums"
                >
                  {deg}°
                </text>
                <line x1={X0} y1={yOf(deg)} x2={X1} y2={yOf(deg)} stroke="currentColor" strokeWidth={0.6} opacity={0.45} />
              </g>
            ))}
            <line
              x1={xOf(0)}
              y1={Y0}
              x2={xOf(0)}
              y2={Y1}
              className="stroke-outline-variant"
              strokeWidth={0.6}
              strokeDasharray="3 4"
            />
          </g>

          {stones.map((s, i) => {
            const live = drag?.id === s.id ? drag : s;
            const cx = xOf(live.u);
            const cy = yOf(live.thetaDeg);
            const on = s.id === selectedId;
            const hot = on || s.id === hoverId || drag?.id === s.id;
            const bead = (s.type ?? state.diamond.type) === "goldBead";
            return (
              <g key={s.id}>
                <circle
                  cx={cx}
                  cy={cy}
                  r={hot ? MARKER_R + 2 : MARKER_R}
                  strokeWidth={on ? 2.4 : 1.2}
                  className={
                    bead
                      ? "fill-secondary-container stroke-secondary"
                      : "fill-surface-container-high stroke-primary"
                  }
                  pointerEvents="none"
                  style={reduce ? undefined : { transition: "r 120ms ease-out" }}
                />
                {on ? (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={MARKER_R + 6}
                    fill="none"
                    className="stroke-secondary"
                    strokeWidth={1}
                    pointerEvents="none"
                  />
                ) : null}
                {/* Transparent grab target, a good bit larger than the dot. */}
                <circle
                  cx={cx}
                  cy={cy}
                  r={MARKER_R + 6}
                  fill="transparent"
                  tabIndex={0}
                  role="button"
                  aria-label={`Stone ${i + 1}: ${Math.round(s.thetaDeg)} degrees, offset ${s.u.toFixed(2)}`}
                  style={{ cursor: "grab" }}
                  onPointerDown={(e) => startDrag(e, s)}
                  onPointerMove={moveDrag}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                  onPointerEnter={() => setHoverId(s.id)}
                  onPointerLeave={() => setHoverId((h) => (h === s.id ? null : h))}
                  onFocus={() => setSelectedId(s.id)}
                  onKeyDown={(e) => onMarkerKey(e, s)}
                />
              </g>
            );
          })}
        </svg>

        <span className="text-center font-body text-[10px] text-outline">
          Down the strip = around the ring · across = the {state.widthMM.toFixed(1)} mm band width.
          Click to place, drag to move, Delete to remove. Not to scale.
        </span>
      </div>

      {/* ── the selected stone, without dragging ─────────────────────────── */}
      {selected ? (
        <SelectedStonePanel
          stone={selected}
          index={stones.indexOf(selected)}
          total={stones.length}
          onRemove={() => remove(selected.id)}
        />
      ) : null}

      {/* ── pattern stamps ───────────────────────────────────────────────── */}
      <div className="flex flex-col gap-2">
        <SectionLabel>Patterns</SectionLabel>
        <div className="grid grid-cols-3 gap-2">
          {servedPatterns.map((p) => (
            <OptionCard
              key={p.id}
              selected={false}
              title={`${p.label} — ${p.count} stones`}
              onClick={() => {
                setSelectedId(null);
                setLastPattern(p.id);
                setPatternCount(p.count);
                // A fresh tile arrives at its own span, so the Spread slider
                // shows what it really is rather than carrying the last one over.
                setPatternSpan(Math.round(freePatternSpanDeg(p.id)));
                void apply(r.applyFreePattern(p.id));
              }}
            >
              <PatternThumb id={p.id} stepDeg={stepDeg} stepU={stepU} />
              <span className="font-display text-[9px] uppercase tracking-[0.1em] text-on-surface-variant">
                {p.label}
              </span>
            </OptionCard>
          ))}
        </div>
        <span className="font-body text-[10px] text-outline">A pattern replaces the current arrangement.</span>

        {/* ITEM 22 — density and reach, for the two halves of "I want to
            increase Number of diamonds in Patterns, and now in wave it goes
            extremely left to right now just how we have very less part covered".

            Both keep the tile's OWN values until moved, so a stamp behaves
            exactly as before unless asked otherwise. `count` only reaches the
            stamps that are curves — re-sampling a Scatter or a Halo would just
            make a different one, and the served `resample` flag says which is
            which. Re-stamping on change is what makes these live: a pattern is
            a one-shot action that writes `freeStones`. */}
        {caps.ringFreePatternV52 ? (
          <>
            <Slider
              label="Stones in Pattern"
              min={3}
              max={40}
              step={1}
              value={patternCount}
              onChange={(v) => {
                setPatternCount(v);
                if (lastPattern) void r.applyFreePattern(lastPattern, { count: v, spanDeg: patternSpan });
              }}
            />
            <Slider
              label="Pattern Spread"
              min={10}
              max={360}
              step={5}
              unit="°"
              value={patternSpan}
              onChange={(v) => {
                setPatternSpan(v);
                if (lastPattern) void r.applyFreePattern(lastPattern, { count: patternCount, spanDeg: v });
              }}
            />
            <span className="font-body text-[10px] text-outline">
              {lastPattern
                ? "Re-stamps the pattern above. Spread is how much of the ring it wraps around."
                : "Pick a pattern first — these re-stamp it."}
            </span>
          </>
        ) : null}
      </div>

      {/* ── arrangement-level controls ───────────────────────────────────── */}
      <Slider
        label="Rotate on the Ring"
        min={-180}
        max={180}
        step={1}
        unit="°"
        value={rotate}
        onChange={(v) => void r.setFreeRotate(v)}
      />

      <div className="flex flex-col gap-2">
        <SectionLabel>Repeat</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: Math.max(REPEAT_BASE, repeat) }, (_, i) => i + 1).map((n) => (
            <Chip key={n} selected={repeat === n} onClick={() => void apply(r.setFreeRepeat(n))}>
              {n === 1 ? "1×" : String(n)}
            </Chip>
          ))}
          {repeat < REPEAT_MAX ? (
            <Chip selected={false} onClick={() => void apply(r.setFreeRepeat(repeat + 1))}>
              + Add
            </Chip>
          ) : null}
        </div>
      </div>

      {/* ── Custom Settings — WHAT stone is being quoted ──────────────────────
          Specification metadata: none of the three reaches the gem shader, they
          move the price estimate and the spec sheet. Run-level (the reference's
          panel is too), so it covers every stone in the arrangement. Gated on
          the served catalog, which is how we know the viewer is v43+. */}
      {(catalogs.diamondOrigins?.length ?? 0) > 0 ? (
        <Disclosure label="Custom Settings" value={specSummary}>
          <Field label="Diamond Origin">
            <Select
              variant="boxed"
              label="Diamond Origin"
              value={d.origin ?? "natural"}
              options={catalogs.diamondOrigins ?? []}
              onChange={(v) => void apply(r.setDiamond({ origin: v as DiamondOrigin }))}
            />
          </Field>
          <Field label="Color Quality (GIA)">
            <Select
              variant="boxed"
              label="Color Quality (GIA)"
              value={d.colorGrade ?? ""}
              options={[UNSPECIFIED, ...(catalogs.gemColorGrades ?? [])]}
              onChange={(v) => void apply(r.setDiamond({ colorGrade: (v || undefined) as GiaColorGrade | undefined }))}
            />
          </Field>
          <Field label="Diamond Clarity">
            <Select
              variant="boxed"
              label="Diamond Clarity"
              value={d.clarity ?? ""}
              options={[UNSPECIFIED, ...(catalogs.gemClarities ?? [])]}
              onChange={(v) => void apply(r.setDiamond({ clarity: (v || undefined) as DiamondClarity | undefined }))}
            />
          </Field>
        </Disclosure>
      ) : null}

      <div className="flex items-center justify-between gap-3">
        <span className="font-body text-[11px] tabular-nums text-on-surface-variant">
          {stones.length ? countLine : "No stones placed yet — click the strip or pick a pattern."}
        </span>
        {stones.length ? (
          <Button
            variant="ghost"
            onClick={() => {
              setSelectedId(null);
              void apply(r.setFreeStones([]));
            }}
          >
            Clear
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The selected stone's own rows (angle, offset, size, type, colour and, from
 * v67, its setting), without dragging. Exported so `check-ring-designs-w1` can
 * render it against a stamped stone.
 */
export function SelectedStonePanel({ stone: selected, index, total, onRemove }: {
  stone: FreeStone;
  index: number;
  total: number;
  onRemove: () => void;
}) {
  const { state, catalogs, r, apply } = useRing();
  const caps = useCaps();
  const perStoneCt = state.spec?.perStoneCarat ?? 0;
  const d = state.diamond;
  const runType = d.type ?? "diamond";
  // A stone with no override follows the run — show that as the active chip.
  const runColor = d.color ?? "white";
  const settingChips = freeStoneSettingChips(caps);
  // A held setting needs the run's stone to be a BARE cut, the Gems tab's rule:
  // the stone shape is run-level, so a prong composite would carry its own head.
  const bareRun = !(catalogs.stoneCuts?.length) || catalogs.stoneCuts.some((c) => c.bare === d.stoneShape);
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-outline-variant/40 bg-surface-container-lowest p-4">
      <div className="flex items-center justify-between">
        <SectionLabel>Selected Stone</SectionLabel>
        <span className="font-body text-[10px] text-outline">
          {index + 1} of {total}
        </span>
      </div>
      <NumberField
        label="Angle"
        min={0}
        max={360}
        step={1}
        unit="°"
        format={(v) => v.toFixed(0)}
        value={selected.thetaDeg}
        onChange={(v) => void r.updateFreeStone(selected.id, { thetaDeg: v })}
      />
      <NumberField
        label="Across width"
        min={-1}
        max={1}
        step={0.01}
        format={(v) => v.toFixed(2)}
        value={selected.u}
        onChange={(v) => void r.updateFreeStone(selected.id, { u: v })}
      />
      <NumberField
        label="Size"
        min={0.001}
        max={3}
        step={0.001}
        unit="ct"
        format={(v) => v.toFixed(3)}
        // The carat the stone is DRAWN at: a stamped stone's `sizeScale`
        // wins over `sizeCt`, so the field reads and writes through the
        // engine's precedence (`../junctionUi`). Writing `sizeCt` alone left
        // every stamped stone unchanged — the Size bug.
        value={freeStoneCarat(selected, perStoneCt)}
        onChange={(v) => void r.updateFreeStone(selected.id, freeStoneSizePatch(v))}
      />
      {/* v67 per-stone hardware. Absent = follow the row's Diamond Setting. */}
      {settingChips.length ? (
        <div className="flex flex-col gap-1.5">
          <span className="font-body text-[10px] uppercase tracking-[0.1em] text-outline">Setting</span>
          <div className="flex flex-wrap gap-1.5">
            <Chip
              selected={selected.setting === undefined}
              onClick={() => void apply(r.updateFreeStone(selected.id, { setting: undefined }))}
            >
              Same as row
            </Chip>
            {settingChips.map((c) => {
              const on = selected.setting === c.id;
              return (
                <Chip
                  key={c.id}
                  selected={on}
                  disabled={!bareRun && !on}
                  title={bareRun || on ? undefined : `${c.label} sets the stone into the metal, so it cannot carry a prong head — set the Gems tab's Prong to None first`}
                  onClick={() => { if (bareRun || on) void apply(r.updateFreeStone(selected.id, { setting: c.id })); }}
                >
                  {c.label}
                </Chip>
              );
            })}
          </div>
        </div>
      ) : null}
      {/* Per-stone type + colour. The engine and the contract have honoured these since
          v40 — and this editor already tints its markers from them — so without these two
          rows the fields were reachable by nothing. Undefined means "follow the run". */}
      <div className="flex flex-col gap-1.5">
        <span className="font-body text-[10px] uppercase tracking-[0.1em] text-outline">Type</span>
        <div className="flex flex-wrap gap-1.5">
          {/* Item 16 — Gold bead is retired. Same rule as the Gems tab's own
              Type row: HIDDEN rather than deleted, and still offered on a
              stone that already IS one so it can be switched back, so no
              saved arrangement changes shape or price on load. */}
          {STONE_TYPE_OPTIONS.filter(
            (t) => t.id !== "goldBead" || (selected.type ?? runType) === "goldBead",
          ).map((t) => (
            <Chip
              key={t.id}
              selected={(selected.type ?? runType) === t.id}
              onClick={() => void r.updateFreeStone(selected.id, { type: t.id })}
            >
              {t.label}
            </Chip>
          ))}
        </div>
      </div>

      {(selected.type ?? runType) === "diamond" ? (
        <div className="flex flex-col gap-1.5">
          <span className="font-body text-[10px] uppercase tracking-[0.1em] text-outline">Colour</span>
          <div className="flex flex-wrap gap-1.5">
            {catalogs.diamondColors?.map((c) => (
              <Chip
                key={c.id}
                selected={(selected.color ?? runColor) === c.id}
                onClick={() => void r.updateFreeStone(selected.id, { color: c.id as FreeStone["color"] })}
              >
                {c.label}
              </Chip>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-1 flex gap-2">
        <Button variant="outline" full onClick={onRemove}>Remove Stone</Button>
      </div>
    </div>
  );
}

/**
 * A pattern tile's icon, drawn from the very positions `applyFreePattern` will
 * stamp — same generator, same pitch, so the icon cannot drift from the stamp.
 *
 * ACROSS is plotted as the band itself: `u` ±1 is edge to edge, so the tile shows
 * how much of the width a stamp really uses. ALONG is normalised to fill the tile
 * rather than drawn to the same scale — 360° of ring against a 4 mm width is the
 * 50:1 ribbon the strip above already refuses to draw, and at 38 px every stamp
 * would collapse into one horizontal smear. Same axes as the strip: down the tile
 * is around the ring.
 */
function PatternThumb({ id, stepDeg, stepU }: { id: FreePatternId; stepDeg: number; stepU: number }) {
  const pts = buildFreePattern(id, { stepDeg, stepU }).map((s) =>
    // Stamps are centred on 0°, and the setter wraps into 0–360 on the way in.
    [s.u, s.thetaDeg > 180 ? s.thetaDeg - 360 : s.thetaDeg] as const,
  );
  const along = Math.max(1e-3, ...pts.map(([, deg]) => Math.abs(deg)));

  return (
    <svg width={38} height={38} viewBox="-19 -19 38 38" aria-hidden="true" className="text-primary">
      {pts.map(([u, deg], i) => (
        <circle key={i} cx={u * 13} cy={(deg / along) * 13} r={2.1} fill="currentColor" />
      ))}
    </svg>
  );
}
